<template>
    <vx-modal v-model:visible="visible" size="md" padding="standard">
        <template v-slot:header>
            <div class="text-2xl">Create Migration</div>
        </template>

        <div class="flex flex-col gap-4">
            <div>
                <vx-label size="lg">Target Team</vx-label>
                <vx-selectable
                    v-model="model.target_team_id"
                    data-test="target-team-selectable"
                    placeholder="Select target team..."
                    :reduce="(option) => option.value"
                    :loading="loadingTeams"
                    :options="teams"
                    @search="searchTeams"
                    :clearable="false"
                    :error="displayFirstError(validate.target_team_id.$errors)"
                >
                </vx-selectable>
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 md:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow md:w-20 md:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    :loading="submitting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Create
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { adminTeamMigrationsClient, adminTeamsClient } from '~/services';
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { displayFirstError, validateRules } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import debounce from '~/utils/debounce';
import { useToasted } from '~/composables/useToasted';

const toasted = useToasted();
const { $externalResults, catchError } = useFormRequestHandler();

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = ref({
    target_team_id: null,
});

const validationRules = computed(() => ({
    target_team_id: {
        required,
    },
}));

const validate = useVuelidate(validationRules, model, { $externalResults });

const emit = defineEmits(['created']);
const submitting = ref(false);
const teams = ref([]);
const loadingTeams = ref(false);

watch(visible, (newVisible) => {
    if (!newVisible) {
        model.value.target_team_id = null;
        validate.value.$reset();
    }
});

const searchTeams = debounce((query) => {
    loadingTeams.value = true;

    adminTeamsClient
        .getTeams({
            query: query,
        })
        .then((response) => {
            teams.value = response.data.data.map((team) => ({
                value: team.id,
                label: `${team.id} - ${team.name}`.trim(),
            }));
        })
        .catch((e) => {
            console.error(e);
            toasted.global.platform_error();
        })
        .finally(() => {
            loadingTeams.value = false;
        });
}, 300);

searchTeams();

const submit = async () => {
    const isValid = await validateRules(validate);

    if (!isValid) {
        return;
    }

    if (submitting.value) {
        return;
    }

    submitting.value = true;

    try {
        const response = await adminTeamMigrationsClient.createMigration({
            target_team_id: model.value.target_team_id,
        });

        emit('created', response.data);

        window.location.assign(`/kiosk/migrations/${response.data.id}/config`);
    } catch (e) {
        catchError(e);
        submitting.value = false;
    }
};
</script>
